import { Reward } from 'features/reward/types';
import { isEngineReward } from 'features/reward/utils';

import { sanitizeSanityBlockContent } from 'components/sanity-block-renderer/helpers';
import { LoyaltyOfferWithInlineAlertMessage } from 'state/loyalty/hooks/types';
import { LoyaltyReward, isReward } from 'state/loyalty/types';
import { getLoyaltyOfferKind } from 'state/loyalty/utils';
import { maybeLocale, maybeLocaleImages } from 'utils/graphql';
import { blockContentToPlainText } from 'utils/sanity';

export interface RewardCardIncentive {
  id: string;
  name: string;
  imageDescription: string;
  image?: unknown;
  backgroundImage?: unknown;
}

const isEngineShapedIncentive = (
  incentive: LoyaltyReward | LoyaltyOfferWithInlineAlertMessage | RewardCardIncentive | Reward
): incentive is RewardCardIncentive | Reward => 'id' in incentive && !('_id' in incentive);

const readPartnerOfferType = (incentive: object): string | undefined => {
  if (!('partnerOfferType' in incentive)) {
    return undefined;
  }

  const { partnerOfferType } = incentive;
  return typeof partnerOfferType === 'string' ? partnerOfferType : undefined;
};

const toSanityBlocks = (value: unknown) => {
  if (!value) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.localeRaw)) {
      return record.localeRaw;
    }
    if (Array.isArray(record.enRaw)) {
      return record.enRaw;
    }
    if (Array.isArray(record.locale)) {
      return record.locale;
    }
  }
  return undefined;
};

const parseLegacyIncentiveData = (
  incentive: LoyaltyReward | LoyaltyOfferWithInlineAlertMessage
) => {
  const incentiveData = isReward(incentive)
    ? {
        description: incentive?.description?.locale,
        image: maybeLocale(incentive?.image),
        imageDescription: incentive?.imageDescription?.locale || '',
        name: maybeLocale(incentive?.name) || '',
        moreInfo: incentive?.moreInfo?.enRaw,
        isOffer: false,
        metadata: null,
      }
    : {
        description: incentive?.description?.localeRaw,
        image: maybeLocaleImages(incentive?.localizedImage),
        imageDescription: '',
        name: blockContentToPlainText(incentive?.name?.localeRaw),
        moreInfo: incentive?.moreInfo?.localeRaw,
        isOffer: true,
        offerServiceMode: incentive?.offerServiceMode || '',
        metadata: incentive?.metadata,
        inlineAlertMessage: incentive?.inlineAlertMessage,
        endDate: incentive?.endDate ?? undefined,
        expirationDate: incentive?.expirationDate ?? undefined,
        offerKind: getLoyaltyOfferKind(incentive),
        partnerOfferType: readPartnerOfferType(incentive),
      };

  return {
    id: incentive?._id,
    backgroundImage: incentive?.backgroundImage,
    loyaltyEngineId: incentive?.loyaltyEngineId ?? '',
    ...incentiveData,
    description: incentiveData.description
      ? sanitizeSanityBlockContent(incentiveData.description)
      : undefined,
  };
};

type LegacyIncentiveData = ReturnType<typeof parseLegacyIncentiveData>;
type EngineIncentiveData = {
  id: string;
  backgroundImage: unknown;
  description: ReturnType<typeof sanitizeSanityBlockContent> | undefined;
  image: unknown;
  imageDescription: string;
  loyaltyEngineId: string;
  metadata: unknown;
  moreInfo: unknown;
  name: string;
  isOffer: false;
  offerServiceMode: '';
  inlineAlertMessage: string | undefined;
  endDate: string | undefined;
  expirationDate: undefined;
  partnerOfferType: string | undefined;
};

type ParsedIncentiveData = EngineIncentiveData | LegacyIncentiveData;

export function parseIncentiveData(incentive: RewardCardIncentive | Reward): EngineIncentiveData;
export function parseIncentiveData(
  incentive: LoyaltyReward | LoyaltyOfferWithInlineAlertMessage
): LegacyIncentiveData;
export function parseIncentiveData(
  incentive: LoyaltyReward | LoyaltyOfferWithInlineAlertMessage | RewardCardIncentive | Reward
): ParsedIncentiveData {
  if (isEngineShapedIncentive(incentive)) {
    const description = isEngineReward(incentive)
      ? toSanityBlocks(incentive.description)
      : undefined;
    const moreInfo = isEngineReward(incentive) ? toSanityBlocks(incentive.moreInfo) : undefined;

    return {
      id: incentive.id,
      backgroundImage: incentive.backgroundImage,
      description: description ? sanitizeSanityBlockContent(description) : undefined,
      // Service rewards are mapped as { locale: <Sanity image> } for legacy consumers.
      // Picture expects the Sanity image itself, not the locale wrapper.
      image: maybeLocale(incentive.image as { locale?: unknown } | null) ?? incentive.image,
      imageDescription: incentive.imageDescription,
      loyaltyEngineId: incentive.id,
      metadata: isEngineReward(incentive) ? incentive.metadata ?? null : null,
      moreInfo,
      name: incentive.name,
      isOffer: false,
      offerServiceMode: '',
      inlineAlertMessage: isEngineReward(incentive) ? incentive.inlineAlertMessage : undefined,
      endDate: isEngineReward(incentive) ? incentive.endDate : undefined,
      expirationDate: undefined,
      partnerOfferType: readPartnerOfferType(incentive),
    };
  }

  return parseLegacyIncentiveData(incentive);
}
